import React, { useState } from 'react'
import { ArrowLeft, CheckCircle, Circle } from '@phosphor-icons/react'
import { Link } from 'react-router-dom'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
// Shares the same SCSS as CreateVoucherPage — import from shared location
import './CreateVoucherPage.scss'

// ─── Shared sub-components (same as CreateVoucherPage) ───────────────────────

const OUTLET_OPTIONS = [
  'Sunway Outlet', 'KLCC Outlet', 'TRX Outlet',
  'Mid Valley Outlet', 'Pavilion Outlet',
]

const ORDER_TYPES = ['Dine-in', 'Pickup', 'Kiosk']

function OptionCard({ active, onClick, title, description }) {
  return (
    <button
      type="button"
      className={`vb-option ${active ? 'vb-option--active' : ''}`}
      onClick={onClick}
    >
      <span className="vb-option__radio">
        {active
          ? <CheckCircle size={22} weight="fill" className="vb-option__radio--checked" />
          : <Circle size={22} className="vb-option__radio--empty" />
        }
      </span>
      <span className="vb-option__body">
        <strong>{title}</strong>
        <span>{description}</span>
      </span>
    </button>
  )
}

function FormGroup({ label, hint, optional, error, children }) {
  return (
    <div className="vb-field">
      {label && (
        <label className="vb-field__label">
          {label}
          {optional && <span className="vb-field__optional">optional</span>}
        </label>
      )}
      {children}
      {error && <span className="vb-field__error">{error}</span>}
      {hint && !error && <p className="vb-field__hint">{hint}</p>}
    </div>
  )
}

function PrefixInput({ prefix, suffix, value, onChange, placeholder, disabled, error }) {
  return (
    <div className={`vb-prefix-input ${error ? 'vb-prefix-input--error' : ''} ${disabled ? 'vb-prefix-input--disabled' : ''}`}>
      {prefix && <span className="vb-prefix-input__tag">{prefix}</span>}
      <input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
      />
      {suffix && <span className="vb-prefix-input__suffix">{suffix}</span>}
    </div>
  )
}

// ─── Create Promo Code ────────────────────────────────────────────────────────

export default function CreatePromoCodePage() {
  const [discountType, setDiscountType]   = useState('percentage')
  const [scope, setScope]                 = useState('merchant')
  const [selectedOutlets, setSelectedOutlets] = useState([])
  const [orderTypes, setOrderTypes]       = useState(['Dine-in'])
  const [validFromEnabled, setValidFromEnabled] = useState(false)
  const [validUntilEnabled, setValidUntilEnabled] = useState(false)
  const [validFrom, setValidFrom]         = useState(null)
  const [validUntil, setValidUntil]       = useState(null)

  const [formData, setFormData] = useState({
    promoCode: '', discountValue: '', maxDiscountCap: '',
    totalClaimLimit: '', usageLimitPerCustomer: '',
    minimumOrderSpend: '', terms: '',
  })
  const [errors, setErrors] = useState({})

  const handleChange = (field, value) => {
    setFormData((p) => ({ ...p, [field]: value }))
    setErrors((p) => ({ ...p, [field]: '' }))
  }

  const toggleOutlet = (o) =>
    setSelectedOutlets((p) => p.includes(o) ? p.filter((x) => x !== o) : [...p, o])

  const toggleOrderType = (t) =>
    setOrderTypes((p) => p.includes(t) ? p.filter((x) => x !== t) : [...p, t])

  const validate = () => {
    const e = {}
    if (!formData.promoCode.trim())      e.promoCode      = 'Promo code is required'
    if (!formData.discountValue.trim())  e.discountValue  = 'Discount amount is required'
    else if (!/^\d*\.?\d+$/.test(formData.discountValue)) e.discountValue = 'Must be numeric'
    if (formData.maxDiscountCap && !/^\d*\.?\d+$/.test(formData.maxDiscountCap)) e.maxDiscountCap = 'Must be numeric'
    if (formData.minimumOrderSpend && !/^\d*\.?\d+$/.test(formData.minimumOrderSpend)) e.minimumOrderSpend = 'Must be numeric'
    if (validFromEnabled && !validFrom)   e.validFrom  = 'Select a start date'
    if (validUntilEnabled && !validUntil) e.validUntil = 'Select an end date'
    if (scope === 'outlet' && !selectedOutlets.length) e.selectedOutlets = 'Select at least one outlet'
    if (validFrom && validUntil && validUntil < validFrom) e.validUntil = 'Must be after start date'
    setErrors(e)
    return !Object.keys(e).length
  }

  const handlePublish = () => { if (validate()) console.log('publish', formData) }

  return (
    <div className="vb-page">

      {/* ── Header ── */}
      <div className="vb-page__header">
        <div className="vb-page__breadcrumb">
          <Link to="/vouchers" className="vb-page__back"><ArrowLeft size={16} /> Back</Link>
          <span className="vb-page__crumb">Vouchers</span>
          <span className="vb-page__sep">/</span>
          <span className="vb-page__crumb">Create promo code</span>
          <span className="vb-page__badge vb-page__badge--purple">Manual checkout entry</span>
        </div>
        <div className="vb-page__actions">
          <button className="vb-btn vb-btn--ghost">Discard</button>
          <button className="vb-btn vb-btn--outline">Save draft</button>
          <button className="vb-btn vb-btn--primary" onClick={handlePublish}>Publish</button>
        </div>
      </div>

      {/* ── Card 1: Promo details ── */}
      <div className="vb-card">
        <div className="vb-card__header">Promo details</div>
        <div className="vb-card__body">

          <FormGroup
            label="Custom promo code"
            hint="Alphanumeric only, no spaces. Customers enter this exactly at checkout. Not case-sensitive."
            error={errors.promoCode}
          >
            <input
              className={`vb-input ${errors.promoCode ? 'vb-input--error' : ''}`}
              type="text"
              placeholder="e.g. SAVE20"
              value={formData.promoCode}
              onChange={(e) => handleChange('promoCode', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
            />
          </FormGroup>

          <FormGroup label="Discount type">
            <div className="vb-option-grid">
              <OptionCard
                active={discountType === 'percentage'}
                onClick={() => setDiscountType('percentage')}
                title="Percentage off"
                description="A % discount on the order subtotal. E.g. 10% off."
              />
              <OptionCard
                active={discountType === 'fixed'}
                onClick={() => setDiscountType('fixed')}
                title="Fixed amount off"
                description="A fixed RM value deducted from the order total. E.g. RM5 off any order."
              />
            </div>
          </FormGroup>

          <div className="vb-form-row">
            <FormGroup
              label={discountType === 'percentage' ? 'Discount percentage' : 'Discount amount'}
              hint="Applied to subtotal before tax"
              error={errors.discountValue}
            >
              <PrefixInput
                prefix={discountType === 'fixed' ? 'RM' : undefined}
                suffix={discountType === 'percentage' ? '%' : undefined}
                value={formData.discountValue}
                placeholder="0"
                error={errors.discountValue}
                onChange={(e) => { if (/^\d*\.?\d*$/.test(e.target.value)) handleChange('discountValue', e.target.value) }}
              />
            </FormGroup>

            <FormGroup
              label="Max discount cap"
              optional
              hint="Limits the max RM saved per redemption"
              error={errors.maxDiscountCap}
            >
              <PrefixInput
                prefix="RM"
                placeholder="No cap"
                value={formData.maxDiscountCap}
                error={errors.maxDiscountCap}
                onChange={(e) => { if (/^\d*\.?\d*$/.test(e.target.value)) handleChange('maxDiscountCap', e.target.value) }}
              />
            </FormGroup>
          </div>

          <FormGroup label="Terms and conditions (if applicable)">
            <textarea
              className="vb-textarea"
              rows={8}
              value={formData.terms}
              onChange={(e) => handleChange('terms', e.target.value)}
            />
          </FormGroup>

        </div>
      </div>

      {/* ── Card 2: Scope & usage rules ── */}
      <div className="vb-card">
        <div className="vb-card__header">Scope &amp; usage rules</div>
        <div className="vb-card__body">

          <FormGroup label="Scope">
            <div className="vb-option-grid">
              <OptionCard
                active={scope === 'merchant'}
                onClick={() => setScope('merchant')}
                title="Merchant-wide"
                description="Available at all outlets. Customers at any branch can redeem this reward."
              />
              <OptionCard
                active={scope === 'outlet'}
                onClick={() => setScope('outlet')}
                title="Outlet specific"
                description="Available only at selected outlet(s) you select below."
              />
            </div>

            {scope === 'outlet' && (
              <div className="vb-outlet-picker">
                <p className="vb-outlet-picker__label">Select outlets</p>
                <div className="vb-chips">
                  {OUTLET_OPTIONS.map((o) => (
                    <button
                      key={o}
                      type="button"
                      className={`vb-chip ${selectedOutlets.includes(o) ? 'vb-chip--active' : ''}`}
                      onClick={() => toggleOutlet(o)}
                    >
                      {o}
                    </button>
                  ))}
                </div>
                {errors.selectedOutlets && <span className="vb-field__error">{errors.selectedOutlets}</span>}
                <p className="vb-field__hint">Customers can use this promo code only at selected outlets.</p>
              </div>
            )}
          </FormGroup>

          <div className="vb-form-row">
            <FormGroup label="Total claim limit" hint="Leave blank for no limit">
              <input
                className="vb-input"
                type="text"
                inputMode="numeric"
                placeholder="Unlimited"
                value={formData.totalClaimLimit}
                onChange={(e) => { if (/^\d*$/.test(e.target.value)) handleChange('totalClaimLimit', e.target.value) }}
              />
            </FormGroup>
            <FormGroup label="Usage limit per customer" hint="Leave blank for no limit">
              <input
                className="vb-input"
                type="text"
                inputMode="numeric"
                placeholder="Unlimited"
                value={formData.usageLimitPerCustomer}
                onChange={(e) => { if (/^\d*$/.test(e.target.value)) handleChange('usageLimitPerCustomer', e.target.value) }}
              />
            </FormGroup>
          </div>

          <div className="vb-form-row">
            <FormGroup label="Minimum order spend" hint="Limits the max RM saved per redemption" error={errors.minimumOrderSpend}>
              <PrefixInput
                prefix="RM"
                placeholder="None"
                value={formData.minimumOrderSpend}
                error={errors.minimumOrderSpend}
                onChange={(e) => { if (/^\d*\.?\d*$/.test(e.target.value)) handleChange('minimumOrderSpend', e.target.value) }}
              />
            </FormGroup>
            <FormGroup label="Valid for order types">
              <div className="vb-chips">
                {ORDER_TYPES.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={`vb-chip ${orderTypes.includes(t) ? 'vb-chip--active' : ''}`}
                    onClick={() => toggleOrderType(t)}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </FormGroup>
          </div>

          <div className="vb-form-row">
            <FormGroup hint="No start date restriction" error={errors.validFrom}>
              <div className="vb-field__label-row">
                <label className="vb-field__label">Valid from</label>
                <label className="vb-checkbox">
                  <input type="checkbox" checked={validFromEnabled} onChange={(e) => { setValidFromEnabled(e.target.checked); if (!e.target.checked) setValidFrom(null) }} />
                  <span>Enable</span>
                </label>
              </div>
              <DatePicker
                selected={validFrom}
                onChange={setValidFrom}
                dateFormat="dd/MM/yyyy"
                placeholderText="dd/mm/yyyy"
                className={`vb-input ${errors.validFrom ? 'vb-input--error' : ''}`}
                disabled={!validFromEnabled}
                onKeyDown={(e) => e.preventDefault()}
              />
            </FormGroup>

            <FormGroup hint="Leave blank for no expiry" error={errors.validUntil}>
              <div className="vb-field__label-row">
                <label className="vb-field__label">Valid until</label>
                <label className="vb-checkbox">
                  <input type="checkbox" checked={validUntilEnabled} onChange={(e) => { setValidUntilEnabled(e.target.checked); if (!e.target.checked) setValidUntil(null) }} />
                  <span>Enable</span>
                </label>
              </div>
              <DatePicker
                selected={validUntil}
                onChange={setValidUntil}
                dateFormat="dd/MM/yyyy"
                placeholderText="dd/mm/yyyy"
                className={`vb-input ${errors.validUntil ? 'vb-input--error' : ''}`}
                disabled={!validUntilEnabled}
                onKeyDown={(e) => e.preventDefault()}
              />
            </FormGroup>
          </div>

        </div>
      </div>
    </div>
  )
}